<script setup lang="ts">
const props = defineProps({
  subheadingTextShade: {
    type: String,
    default: 'dark',
  },
});

const subheadingCSSClasses = computed(() => {
  return [`hero__subheading--${props.subheadingTextShade}`];
});
</script>

<template>
  <div :class="{ 'hero--has-background': $slots.background }" class="hero">
    <slot v-if="$slots.background" name="background" />

    <div class="hero__inner container">
      <div class="hero__left">
        <div v-if="$slots.top" class="hero__top">
          <slot name="top" />
        </div>

        <div v-if="$slots.heading || $slots.subheading" class="hero__middle">
          <h1 v-if="$slots.heading" class="hero__heading">
            <slot name="heading" />
          </h1>

          <div v-if="$slots.subheading" :class="subheadingCSSClasses" class="hero__subheading">
            <slot name="subheading" />
          </div>
        </div>

        <div v-if="$slots.bottom" class="hero__bottom">
          <slot name="bottom" />
        </div>
      </div>

      <div v-if="$slots.right" class="hero__right">
        <slot name="right" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use 'core/colors';
@use 'core/layout/grid-utils';
@use 'core/layout/grid-vars';
@use 'core/typography/fonts';
@use 'core/typography/type';
@use 'vendor/bootstrap/scss/mixins/breakpoints';
@use 'vendor/bootstrap/scss/mixins/grid';

.hero {
  position: relative;
  display: flex;
  flex-direction: column;

  :deep(.hero-background) {
    + .hero__inner {
      padding-top: 0;

      @include breakpoints.media-breakpoint-up(md) {
        bottom: 0;
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
      }
    }
  }

  &__inner {
    @include grid-utils.outer-gutters;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;

    @include breakpoints.media-breakpoint-up(xl) {
      flex-direction: row;
      align-items: flex-end;
    }

    .hero__left {
      &:only-child {
        width: 100%;
      }
    }
  }

  &__top {
    margin-bottom: grid-vars.space(2.5);
  }

  &__middle {
    margin-bottom: grid-vars.space(4, fixed);

    @include breakpoints.media-breakpoint-up(md) {
      margin-bottom: grid-vars.space(7, fixed);
      max-width: grid-vars.space(100);
    }

    .hero__subheading {
      @include breakpoints.media-breakpoint-up(md) {
        @include grid.make-col(10);
      }

      @include breakpoints.media-breakpoint-up(xl) {
        @include grid.make-col(8);
      }
    }
  }

  &__heading {
    @include type.text--h1;
    @include fonts.font-size(32);

    @include breakpoints.media-breakpoint-up(md) {
      @include fonts.font-size(42);
    }

    @include breakpoints.media-breakpoint-up(xl) {
      @include fonts.font-size(67);
    }

    margin-bottom: grid-vars.space(2.5);
    letter-spacing: -0.03rem;
  }

  &__subheading {
    @include type.text--subheading;
    @include fonts.font-size(16);

    @include breakpoints.media-breakpoint-up(md) {
      @include fonts.font-size(20);
    }

    @include breakpoints.media-breakpoint-up(xl) {
      @include fonts.font-size(24);
    }

    font-weight: 500;
    line-height: 1.5;
    letter-spacing: -0.02rem;
    padding-top: grid-vars.space(1);

    &--dark {
      color: colors.color(grey, secondary);
    }
  }

  .background {
    height: grid-vars.space(12, fixed);

    @include breakpoints.media-breakpoint-up(md) {
      height: grid-vars.space(42, fixed);
    }
  }
}
</style>
